import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { getLimitsForTier, getUsage, type UserUsage } from '@/lib/cat/usageService'
import { BILLING_PLANS, formatZar } from '@/lib/billingPlans'

type RangeKey = '30' | '90' | 'all'
const RANGES: { key: RangeKey; label: string }[] = [
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
  { key: 'all', label: 'All time' },
]
const ALL_TIME_FROM = '2000-01-01T00:00:00Z'

interface Core {
  words: number
  words_prev: number
  segments: number
  active_projects: number
  projects_total: number
  projects_completed: number
  pairs_total: number
  pairs: { source: string; target: string; words: number }[]
  weeks: { week_start: string; words: number }[]
  tm_entries: number
  tm_reused: number
  tm_added_month: number
  glossary_terms: number
}
interface PremiumStatus { tier: string; events: number; ready: boolean }

const fmt = (n: number) => (n || 0).toLocaleString('en-GB')
const pairLabel = (source: string, target: string) => `${source.toUpperCase()} → ${target.toUpperCase()}`

function rangeFrom(range: RangeKey): string {
  if (range === 'all') return ALL_TIME_FROM
  return new Date(Date.now() - Number(range) * 86_400_000).toISOString()
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function weekLabel(weekStart: string): string {
  const d = new Date(weekStart)
  return `${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]}`
}

function resetsOn(): string {
  const now = new Date()
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
  return `1 ${next.toLocaleString('en-GB', { month: 'long', timeZone: 'UTC' })} ${next.getUTCFullYear()}`
}

const C = {
  ink: '#1E293B',
  slate: '#334155',
  muted: '#475569',
  line: '#E2E8F0',
  gold: '#E2B273',
}
const cardStyle: React.CSSProperties = { background: '#FFFFFF', border: `1px solid ${C.line}`, borderRadius: 14, padding: '22px 24px' }
const h2Style: React.CSSProperties = { margin: 0, fontSize: 17, fontWeight: 700 }
const subStyle: React.CSSProperties = { margin: 0, fontSize: 13, color: C.muted }
const goldButtonStyle: React.CSSProperties = { display: 'inline-block', padding: '14px 30px', borderRadius: 8, background: C.gold, color: C.ink, fontSize: 16, fontWeight: 700, textDecoration: 'none' }

function Card({ title, sub, subGap = 16, children, style }: { title?: string; sub?: string; subGap?: number; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <section style={{ ...cardStyle, ...style }}>
      {title && <h2 style={{ ...h2Style, marginBottom: sub ? 4 : 16 }}>{title}</h2>}
      {sub && <p style={{ ...subStyle, marginBottom: subGap }}>{sub}</p>}
      {children}
    </section>
  )
}

function Kpi({ label, value, suffix, note, noteColor }: { label: string; value: string; suffix?: string; note?: string; noteColor?: string }) {
  return (
    <div style={{ background: '#FFFFFF', border: `1px solid ${C.line}`, borderRadius: 14, padding: '20px 22px' }}>
      <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase', color: C.muted }}>{label}</p>
      <p style={{ margin: '0 0 6px', fontSize: 30, fontWeight: 700, color: C.ink }}>
        {value}
        {suffix && <span style={{ fontSize: 16, fontWeight: 500, color: C.muted }}> {suffix}</span>}
      </p>
      <p style={{ margin: 0, fontSize: 13, color: noteColor || C.muted, minHeight: 16 }}>{note || ' '}</p>
    </div>
  )
}

function Meter({ label, right, pct, color, note }: { label: string; right: string; pct?: number; color?: string; note?: string }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ color: C.muted }}>{right}</span>
      </div>
      {pct !== undefined && (
        <div style={{ height: 10, borderRadius: 5, background: C.line, overflow: 'hidden' }} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}>
          <div style={{ width: `${pct}%`, height: 10, borderRadius: 5, background: color }} />
        </div>
      )}
      {note && <p style={{ margin: pct !== undefined ? '8px 0 0' : 0, fontSize: 12, color: C.muted }}>{note}</p>}
    </div>
  )
}

function pctOf(used: number, limit: number): number {
  return limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0
}

// ── Premium section ──────────────────────────────────────────────────────────
// Static sample figures, shown blurred to people without access. They are never
// the user's real data.
const SAMPLE_ORIGINS = [
  { label: 'TM exact match', pct: 18, color: '#1E293B' },
  { label: 'TM fuzzy match', pct: 22, color: '#475569' },
  { label: 'Machine translation', pct: 41, color: '#E2B273' },
  { label: 'Lyra suggestion', pct: 4, color: '#8A5A1F' },
  { label: 'Translated from scratch', pct: 15, color: '#CBD5E1' },
]
const SAMPLE_EDITS = [
  { label: 'DeepL', pct: 14, color: '#E2B273' },
  { label: 'Google', pct: 23, color: '#E2B273' },
  { label: 'TM fuzzy matches', pct: 9, color: '#1E293B' },
]
const SAMPLE_PROJECTS = [
  ['Annual Report 2026', 'EN → PT-PT', '11,425', '64%', '12%', '18 Oct 2026'],
  ['Health Committee Minutes', 'EN → PT-AO', '8,428', '58%', '16%', '12 Oct 2026'],
  ['Investor Brief', 'PT-PT → EN', '2,400', '41%', '21%', '3 Oct 2026'],
  ['Product Manual', 'EN → FR', '780', '35%', '19%', '28 Sep 2026'],
]
const tableCols: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) repeat(5, minmax(0, 1fr))', gap: 12 }

interface PremiumCardDef { key: string; title: string; sub: string; wide?: boolean; sample: React.ReactNode }

const PREMIUM_CARDS: PremiumCardDef[] = [
  {
    key: 'origins', title: 'Where your words came from', sub: 'How each confirmed segment was produced',
    sample: (
      <>
        <div style={{ display: 'flex', height: 18, borderRadius: 9, overflow: 'hidden', marginBottom: 16 }}>
          {SAMPLE_ORIGINS.map(o => <div key={o.label} style={{ width: `${o.pct}%`, background: o.color }} />)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px 16px', fontSize: 13 }}>
          {SAMPLE_ORIGINS.map(o => (
            <span key={o.label}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: o.color, marginRight: 8 }} />
              {o.label} {o.pct}%
            </span>
          ))}
        </div>
      </>
    ),
  },
  {
    key: 'edits', title: 'Post-editing effort', sub: 'How much of each suggestion you changed before confirming',
    sample: (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {SAMPLE_EDITS.map(e => (
            <div key={e.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 6 }}>
                <span style={{ fontWeight: 600 }}>{e.label}</span><span style={{ color: C.muted }}>{e.pct}% edited</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: '#F1F5F9' }}><div style={{ width: `${e.pct}%`, height: 8, borderRadius: 4, background: e.color }} /></div>
            </div>
          ))}
        </div>
        <p style={{ margin: '16px 0 0', fontSize: 13, color: C.ink, background: '#FDF6EC', borderRadius: 8, padding: '10px 12px' }}>For EN → PT-PT, DeepL needed the fewest edits.</p>
      </>
    ),
  },
  {
    key: 'saved', title: 'Time and value saved', sub: "Words you didn't have to translate from scratch",
    sample: (
      <div style={{ display: 'flex', gap: 32 }}>
        <div><p style={{ margin: 0, fontSize: 34, fontWeight: 700, color: C.gold }}>≈ 21 h</p><p style={{ margin: '4px 0 0', fontSize: 13, color: '#CBD5E1' }}>saved this month</p></div>
        <div><p style={{ margin: 0, fontSize: 34, fontWeight: 700, color: C.gold }}>≈ $310</p><p style={{ margin: '4px 0 0', fontSize: 13, color: '#CBD5E1' }}>at your rate of $0.06 a word</p></div>
      </div>
    ),
  },
  {
    key: 'productivity', title: 'Your productivity', sub: 'Based on your active time in the editor',
    sample: (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        {[['612', 'words per active hour'], ['Tuesday', 'your most productive day'], ['38 s', 'average per segment']].map(([v, l]) => (
          <div key={l}><p style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{v}</p><p style={{ margin: '4px 0 0', fontSize: 12, color: C.muted }}>{l}</p></div>
        ))}
      </div>
    ),
  },
  {
    key: 'records', title: 'Project records', sub: '', wide: true,
    sample: (
      <>
        <div style={{ ...tableCols, padding: '10px 14px', borderRadius: 10, background: C.ink, color: '#FFFFFF', fontSize: 12, fontWeight: 700 }}>
          {['Project', 'Pair', 'Words', 'TM and MT reuse', 'Edit effort', 'Completed'].map(h => <span key={h}>{h}</span>)}
        </div>
        {SAMPLE_PROJECTS.map(r => (
          <div key={r[0]} style={{ ...tableCols, padding: '12px 14px', borderBottom: `1px solid ${C.line}`, fontSize: 13, alignItems: 'center' }}>
            {r.map((c, i) => <span key={i} style={i === 0 ? { fontWeight: 600 } : i === 5 ? { color: C.muted } : undefined}>{c}</span>)}
          </div>
        ))}
      </>
    ),
  },
]

function SampleCard({ def }: { def: PremiumCardDef }) {
  if (def.key === 'saved') {
    return (
      <section style={{ ...cardStyle, background: C.ink, border: 'none', color: '#FFFFFF' }}>
        <h3 style={{ ...h2Style, marginBottom: 4 }}>{def.title}</h3>
        <p style={{ ...subStyle, color: '#CBD5E1', marginBottom: 18 }}>{def.sub}</p>
        {def.sample}
      </section>
    )
  }
  return (
    <section style={{ ...cardStyle, gridColumn: def.wide ? '1 / -1' : undefined }}>
      <h3 style={{ ...h2Style, marginBottom: def.sub ? 4 : 16 }}>{def.title}</h3>
      {def.sub && <p style={{ ...subStyle, marginBottom: 18 }}>{def.sub}</p>}
      {def.sample}
    </section>
  )
}

function CollectingCard({ def, status }: { def: PremiumCardDef; status: PremiumStatus | null }) {
  const events = status?.events ?? 0
  const pct = Math.min(100, Math.round((events / 50) * 100))
  return (
    <Card title={def.title} sub={def.sub || undefined} subGap={14} style={def.wide ? { gridColumn: '1 / -1' } : undefined}>
      {status?.ready ? (
        <p style={{ margin: 0, fontSize: 14, color: C.muted }}>Enough data has been collected. Detailed charts are coming in the next update.</p>
      ) : (
        <>
          <p style={{ margin: 0, fontSize: 14, color: C.muted }}>Collecting data: your first insights appear after about 50 confirmed segments ({events} so far).</p>
          <div style={{ marginTop: 12, height: 8, borderRadius: 4, background: '#F1F5F9', overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: 8, borderRadius: 4, background: C.gold }} /></div>
        </>
      )}
    </Card>
  )
}

function UpgradeOverlay() {
  const plan = BILLING_PLANS.find(p => p.id === 'premium')!
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 110, zIndex: 10 }}>
      <div
        style={{
          width: 460, maxWidth: '100%', boxSizing: 'border-box', padding: '30px 32px', borderRadius: 20,
          background: 'radial-gradient(130% 150% at 0% 0%, #1E293B 0%, #334155 45%, #8C7556 85%, #E2B273 100%)',
          border: '1px solid rgba(226,178,115,0.5)', boxShadow: '0 24px 60px rgba(30,41,59,0.35)', textAlign: 'center',
        }}
      >
        <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', color: C.gold }}>PREMIUM INSIGHTS</p>
        <p style={{ margin: '0 0 14px', fontSize: 22, lineHeight: '30px', fontWeight: 700, color: '#FFFFFF' }}>See where your time really goes</p>
        <div style={{ textAlign: 'left', background: 'rgba(255,255,255,0.88)', borderRadius: 12, padding: '14px 18px', margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, color: C.ink }}>
          <span>· How much of your work came from TM, MT and Lyra</span>
          <span>· Which MT engine needs the fewest edits for each language pair</span>
          <span>· Hours and money saved, at your own rate</span>
          <span>· Your productivity and a full record of every project</span>
        </div>
        <Link to="/billing" style={goldButtonStyle}>Upgrade to Premium</Link>
        <p style={{ margin: '12px 0 0', fontSize: 13, color: '#FDF6EC' }}>${plan.priceUsd} a month (about {formatZar(plan.priceZar)}). Cancel any time.</p>
      </div>
    </div>
  )
}

function PremiumSection({ status }: { status: PremiumStatus | null }) {
  const tier = status?.tier || 'free'
  const blurStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 20, filter: 'blur(7px)', pointerEvents: 'none', userSelect: 'none' }
  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 20 }

  if (tier === 'premium') {
    return (
      <div style={grid}>
        {PREMIUM_CARDS.map(def => <CollectingCard key={def.key} def={def} status={status} />)}
      </div>
    )
  }

  const lockedCards = tier === 'standard' ? PREMIUM_CARDS.slice(1) : PREMIUM_CARDS
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {tier === 'standard' && <CollectingCard def={{ ...PREMIUM_CARDS[0], wide: false }} status={status} />}
      <div style={{ position: 'relative' }}>
        <div style={blurStyle} aria-hidden="true">
          <div style={grid}>{lockedCards.map(def => <SampleCard key={def.key} def={tier === 'standard' && def.key === 'productivity' ? { ...def, wide: true } : def} />)}</div>
        </div>
        <UpgradeOverlay />
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const { user } = useAuth()
  const userId = (user as any)?.id as string | undefined
  const [range, setRange] = useState<RangeKey>('30')
  const [core, setCore] = useState<Core | null>(null)
  const [allTime, setAllTime] = useState<Core | null>(null)
  const [status, setStatus] = useState<PremiumStatus | null>(null)
  const [usage, setUsage] = useState<UserUsage | null>(null)
  const [tier, setTier] = useState<string>('free')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const callCore = useCallback(async (r: RangeKey): Promise<Core> => {
    const { data, error: err } = await supabase.rpc('cat_analytics_core', {
      p_from: rangeFrom(r),
      p_to: new Date().toISOString(),
    })
    if (err) throw err
    return data as Core
  }, [])

  // Things that do not depend on the range chip.
  useEffect(() => {
    if (!userId) return
    let cancelled = false
    ;(async () => {
      try {
        const [all, st, u, prof] = await Promise.all([
          callCore('all'),
          supabase.rpc('cat_analytics_premium_status'),
          getUsage(userId),
          supabase.from('profiles').select('tier').eq('id', userId).maybeSingle(),
        ])
        if (cancelled) return
        setAllTime(all)
        setStatus(st.error ? null : (st.data as PremiumStatus))
        setUsage(u)
        setTier((prof.data as any)?.tier || (st.data as any)?.tier || 'free')
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load your analytics.')
      }
    })()
    return () => { cancelled = true }
  }, [userId, callCore])

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const data = await callCore(range)
        if (!cancelled) { setCore(data); setError(null) }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load your analytics.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [userId, range, callCore])

  const limits = getLimitsForTier(tier)
  const rangeName = range === '30' ? '30 days' : range === '90' ? '90 days' : ''

  const delta = useMemo(() => {
    if (!core || range === 'all' || !(core.words_prev > 0)) return null
    const pct = Math.round(((core.words - core.words_prev) / core.words_prev) * 100)
    if (pct > 0) return { text: `Up ${pct}% on the previous ${rangeName}`, color: '#166534' }
    if (pct < 0) return { text: `Down ${Math.abs(pct)}% on the previous ${rangeName}`, color: '#475569' }
    return { text: `Level with the previous ${rangeName}`, color: '#475569' }
  }, [core, range, rangeName])

  const weeks = core?.weeks ?? []
  const maxWeek = Math.max(1, ...weeks.map(w => w.words))
  const chartLabel = `Words translated per week, last ${weeks.length} weeks: ` + weeks.map(w => `${weekLabel(w.week_start)} ${fmt(w.words)}`).join(', ')
  const pairs = (core?.pairs ?? []).slice().sort((a, b) => b.words - a.words)
  const maxPair = Math.max(1, ...pairs.map(p => p.words))
  const topPair = (allTime?.pairs ?? core?.pairs ?? []).slice().sort((a, b) => b.words - a.words)[0]
  const isEmpty = !!allTime && allTime.segments === 0

  const assets: [string, number | undefined][] = [
    ['TM entries', allTime?.tm_entries ?? core?.tm_entries],
    ['Glossary terms', allTime?.glossary_terms ?? core?.glossary_terms],
    ['TM entries reused', allTime?.tm_reused ?? core?.tm_reused],
    ['Added this month', allTime?.tm_added_month ?? core?.tm_added_month],
  ]
  const chip = (active: boolean): React.CSSProperties => ({
    height: 38, padding: '0 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
    border: `1px solid ${active ? C.ink : '#CBD5E1'}`, background: active ? C.ink : '#FFFFFF', color: active ? '#FFFFFF' : C.ink,
  })

  return (
    <div style={{ padding: '28px 32px 48px', maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24, color: C.ink }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: '0 0 6px', fontSize: 28, fontWeight: 700 }}>Analytics</h1>
          <p style={{ margin: 0, fontSize: 15, color: C.muted }}>Your translation activity at a glance.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} role="group" aria-label="Date range">
          {RANGES.map(r => (
            <button key={r.key} type="button" onClick={() => setRange(r.key)} aria-pressed={range === r.key} style={chip(range === r.key)}>
              {r.label}
            </button>
          ))}
          {/* Export CSV: hidden until Phase 2. */}
        </div>
      </div>

      <style>{`
        @media (max-width: 1100px) { .analytics-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
        @media (max-width: 900px) { .analytics-split { grid-template-columns: minmax(0, 1fr) !important; } }
      `}</style>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">{error}</div>
      )}

      {isEmpty ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <h2 style={{ ...h2Style }}>Your insights start here</h2>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: C.muted }}>Confirm a few segments and your activity will appear.</p>
            <Link to="/projects" style={{ ...goldButtonStyle, marginTop: 16, padding: '10px 22px', fontSize: 14, borderRadius: 999 }}>Go to Projects</Link>
          </div>
        </Card>
      ) : (
        <>
          <div className="analytics-kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 20, opacity: loading ? 0.6 : 1 }}>
            <Kpi label="Words translated" value={core ? fmt(core.words) : '–'} note={delta?.text} noteColor={delta?.color} />
            <Kpi
              label="Segments confirmed"
              value={core ? fmt(core.segments) : '–'}
              note={core && core.active_projects > 0 ? `Across ${fmt(core.active_projects)} ${core.active_projects === 1 ? 'project' : 'projects'}` : undefined}
            />
            <Kpi
              label="Projects completed"
              value={core ? fmt(core.projects_completed) : '–'}
              suffix={core ? `of ${fmt(core.projects_total)}` : undefined}
              note={core ? `${fmt(core.active_projects)} in progress` : undefined}
            />
            <Kpi
              label="Language pairs"
              value={core ? fmt(core.pairs_total) : '–'}
              note={topPair ? `Most used: ${pairLabel(topPair.source, topPair.target)}` : undefined}
            />
          </div>

          <div className="analytics-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 20 }}>
            <section style={cardStyle}>
              <h2 style={{ ...h2Style, marginBottom: 4 }}>Words translated per week</h2>
              <p style={{ ...subStyle, marginBottom: 20 }}>Confirmed segments, last {weeks.length || 8} weeks</p>
              {weeks.length === 0 ? (
                <p style={{ margin: 0, fontSize: 14, color: C.muted }}>No weekly data yet.</p>
              ) : (
                <div role="img" aria-label={chartLabel}>
                  <div style={{ height: 200, display: 'flex', alignItems: 'flex-end', gap: 14, padding: '0 4px', borderBottom: `1px solid ${C.line}` }}>
                    {weeks.map((w, i) => (
                      <div key={w.week_start} style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 6, height: '100%' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>{fmt(w.words)}</span>
                        <div style={{ width: '100%', maxWidth: 46, borderRadius: '6px 6px 0 0', background: i === weeks.length - 1 ? C.gold : C.slate, height: Math.max(2, Math.round((w.words / maxWeek) * 160)) }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 14, padding: '8px 4px 0' }}>
                    {weeks.map(w => (
                      <span key={w.week_start} style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center', fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}>{weekLabel(w.week_start)}</span>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <h2 style={{ ...h2Style, marginBottom: 4 }}>This month's usage</h2>
                <p style={subStyle}>Resets on {resetsOn()}</p>
              </div>
              {usage ? (
                <>
                  <Meter
                    label="Machine translation"
                    right={`${fmt(usage.mt_chars_used)} of ${fmt(limits.mt_chars)} characters`}
                    pct={pctOf(usage.mt_chars_used, limits.mt_chars)}
                    color={C.gold}
                  />
                  <Meter
                    label="Lyra queries"
                    right={`${fmt(usage.ai_queries_used)} of ${fmt(limits.ai_queries)}`}
                    pct={pctOf(usage.ai_queries_used, limits.ai_queries)}
                    color={C.slate}
                  />
                  <Meter label="Top-up characters" right={`${fmt(usage.mt_chars_extra)} remaining`} note="Used only after your monthly allowance runs out." />
                </>
              ) : (
                <p style={{ margin: 0, fontSize: 14, color: C.muted }}>Loading your usage…</p>
              )}
            </section>
          </div>

          <div className="analytics-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 20 }}>
            <Card title="Language pairs">
              {pairs.length === 0 ? (
                <p style={{ margin: 0, fontSize: 14, color: C.muted }}>No language pairs in this period.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {pairs.slice(0, 6).map(p => (
                    <div key={`${p.source}-${p.target}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 6 }}>
                        <span style={{ fontWeight: 600 }}>{pairLabel(p.source, p.target)}</span>
                        <span style={{ color: C.muted }}>{fmt(p.words)} words</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 4, background: '#F1F5F9', overflow: 'hidden' }}>
                        <div style={{ height: 8, borderRadius: 4, background: C.ink, width: `${(p.words / maxPair) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title="Your language assets">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
                {assets.map(([label, value]) => (
                  <div key={label} style={{ background: '#F8FAFC', borderRadius: 10, padding: '14px 16px' }}>
                    <p style={{ margin: '0 0 4px', fontSize: 12, color: C.muted }}>{label}</p>
                    <p style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{value === undefined ? '–' : fmt(value)}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Premium insights</h2>
            {status?.tier !== 'premium' && (
              <span style={{ padding: '4px 12px', borderRadius: 999, background: 'rgba(226,178,115,0.25)', color: '#6B4A1C', fontSize: 12, fontWeight: 700 }}>Premium</span>
            )}
          </div>
          <PremiumSection status={status} />
        </>
      )}
    </div>
  )
}
